import Box from '@mui/material/Box'
import Stack, { type StackProps } from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import { forwardRef, type ReactElement, type ReactNode } from 'react'

import {
  ExtraSmallText,
  Heading3,
  SmallText,
  TextBody,
  type TypographyColor,
} from '../../data-display/typography/Typography'
import { Card } from '../card/Card'
import type { CardBackground } from '../card/Card.styled'

// Tailwind's whitespace-pre-wrap (applied by TextBody/SmallText via bodyBase) must be
// explicitly overridden with !important since emotion and Tailwind compete at the same
// CSS specificity level.
const NOWRAP_SX = {
  whiteSpace: 'nowrap !important',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
} as const

// ─── CardGroup ─────────────────────────────────────────────────────────

export type CardGroupProps = {
  children: ReactNode
  /**
   * Breakpoint at which sections switch from column (stacked) to row (side-by-side).
   * @default 'md'
   */
  breakpoint?: 'sm' | 'md' | 'never' | 'always'
  /**
   * Optional full-width section rendered below the columns, separated by a
   * horizontal divider. Unlike `children`, it never participates in the
   * side-by-side column layout. Wrapped with the standard section padding.
   */
  footer?: ReactNode
  /**
   * Surface the card sits on. Defaults to `transparent`, which shows the page
   * through — pass `surface` for the raised white card.
   */
  background?: CardBackground
  'data-testid'?: string
}

type CardGroupBreakpoint = NonNullable<CardGroupProps['breakpoint']>

const resolveDirection = (
  breakpoint: CardGroupBreakpoint,
): StackProps['direction'] => {
  if (breakpoint === 'never') {
    return 'column'
  }
  if (breakpoint === 'always') {
    return 'row'
  }

  return { xs: 'column', [breakpoint]: 'row' }
}

const resolveSectionFlex = (breakpoint: CardGroupBreakpoint) => {
  if (breakpoint === 'never') {
    return 'none'
  }
  if (breakpoint === 'always') {
    return 1
  }

  return { xs: 'none', [breakpoint]: 1 }
}

const resolveDividerSx = (breakpoint: CardGroupBreakpoint) => {
  if (breakpoint === 'never') {
    return { right: 0, height: '1px' }
  }
  if (breakpoint === 'always') {
    return { bottom: 0, width: '1px' }
  }

  return {
    right: { xs: 0, [breakpoint]: 'auto' },
    height: { xs: '1px', [breakpoint]: 'auto' },
    bottom: { [breakpoint]: 0 },
    width: { [breakpoint]: '1px' },
  }
}

/**
 * Displays a card containing multiple equal-width sections separated by dividers.
 *
 * - Stacks sections vertically below `breakpoint` (divider becomes horizontal)
 * - Lays sections side-by-side at/above `breakpoint` (divider becomes vertical)
 * - With `breakpoint="never"`, sections remain stacked at all sizes (always-horizontal divider)
 * - Side-by-side sections share the width equally (`flex: 1`, `minWidth: 0` to
 *   allow text truncation); stacked sections take the height of their content
 * - An optional `footer` adds a full-width section below the columns
 *
 * Use `<CardGroupContent>` for the standard pattern (label + icon + value + subtitle),
 * or `<CardGroupSection>` for consistent padding with custom content.
 */
export const CardGroup = ({
  children,
  breakpoint = 'md',
  footer,
  background,
  'data-testid': dataTestId,
}: CardGroupProps) => {
  const direction = resolveDirection(breakpoint)
  const sectionFlex = resolveSectionFlex(breakpoint)
  const dividerSx = resolveDividerSx(breakpoint)

  return (
    <Card noPadding background={background} data-testid={dataTestId}>
      <Stack sx={{ width: '100%' }}>
        <Stack
          direction={direction}
          sx={{
            width: '100%',
            '& > *': { flex: sectionFlex, minWidth: 0 },
            '& > *:not(:first-of-type)': {
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                backgroundColor: 'divider',
                top: 0,
                // insetInlineStart for RTL support
                insetInlineStart: 0,
                ...dividerSx,
              },
            },
          }}
        >
          {children}
        </Stack>

        {footer != null && (
          <Box
            sx={{
              width: '100%',
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <CardGroupSection>{footer}</CardGroupSection>
          </Box>
        )}
      </Stack>
    </Card>
  )
}

// ─── CardGroupSection ──────────────────────────────────────────────────

export type CardGroupSectionProps = {
  children: ReactNode
  /**
   * Tightens the horizontal padding to 16px at every width instead of the
   * default 24px from `md` up. For groups that must fit four columns inside a
   * drawer, where the default gutters leave the labels no room.
   */
  dense?: boolean
  'data-testid'?: string
}

/**
 * Section wrapper providing the consistent padding + overflow control used inside
 * `<CardGroup>`. Compose any content inside.
 *
 * Forwards the ref so it can be used as a direct child of `<Tooltip>`.
 */
export const CardGroupSection = forwardRef<
  HTMLDivElement,
  CardGroupSectionProps
>(({ children, dense = false, 'data-testid': dataTestId, ...rest }, ref) => (
  <Stack
    ref={ref}
    data-testid={dataTestId}
    sx={{
      py: 2,
      px: dense ? 2 : { xs: 2, md: 3 },
      minWidth: 0,
      overflow: 'hidden',
    }}
    {...rest}
  >
    {children}
  </Stack>
))
CardGroupSection.displayName = 'CardGroupSection'

// ─── CardGroupContent ──────────────────────────────────────────────────

type SectionLabelColor = Extract<
  TypographyColor,
  'primary' | 'secondary' | 'warning'
>

function OptionalTooltip({
  title,
  children,
}: {
  title?: string
  children: ReactElement
}) {
  return (
    <Tooltip
      title={title ?? ''}
      arrow
      placement="top"
      disableHoverListener={!title}
    >
      {children}
    </Tooltip>
  )
}

/**
 * `body` is the 16px default; `xs` is the 12px/18px label the design system
 * specifies for dense groups.
 */
type SectionLabelSize = 'body' | 'xs'

function SectionLabel({
  testId,
  color,
  wrap = false,
  size = 'body',
  children,
}: {
  testId: string
  color: SectionLabelColor
  wrap?: boolean
  size?: SectionLabelSize
  children: string
}) {
  const Label = size === 'xs' ? ExtraSmallText : TextBody

  return (
    <Label
      data-testid={`${testId}-label`}
      color={color}
      sx={wrap ? { display: 'block' } : { ...NOWRAP_SX, display: 'block' }}
    >
      {children}
    </Label>
  )
}

export type CardGroupContentProps = {
  label: string
  icon: ReactNode
  value: ReactNode
  subtitle?: ReactNode
  testId: string
  tooltip?: string
}

/**
 * Standard section content for `<CardGroup>` with:
 * - a label on top
 * - an icon + value + subtitle on a single row below
 *
 * Adds a tooltip and consistent test ids (`${testId}`, `${testId}-label`, `${testId}-value`, `${testId}-subtitle`).
 */
export function CardGroupContent({
  label,
  icon,
  value,
  subtitle,
  testId,
  tooltip,
}: CardGroupContentProps) {
  return (
    <OptionalTooltip title={tooltip}>
      <CardGroupSection data-testid={testId}>
        <SectionLabel testId={testId} color="primary">
          {label}
        </SectionLabel>
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            gap: 1,
            mt: 0.5,
            flexWrap: 'nowrap',
            overflow: 'hidden',
          }}
        >
          <Stack sx={{ flexShrink: 0 }}>{icon}</Stack>
          <Heading3
            data-testid={`${testId}-value`}
            color="primary"
            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            {value}
          </Heading3>
          {subtitle != null && subtitle !== '' && (
            <SmallText
              data-testid={`${testId}-subtitle`}
              color="secondary"
              sx={NOWRAP_SX}
            >
              {subtitle}
            </SmallText>
          )}
        </Stack>
      </CardGroupSection>
    </OptionalTooltip>
  )
}

// Exported for use in app-specific section variants (e.g. CounterCardContent in HR).
export { OptionalTooltip, SectionLabel, NOWRAP_SX }
